/**
 * useThreadSummonSetting — the per-thread "@Prest0n mention required" control
 * (owner directive 2026-08-06).
 *
 * Thin React glue only. The rule lives in `services/threadMentions/summonGate`
 * (pure, unit-tested); the persisted setting lives on the server-owned
 * membership doc and reaches the client through the `threadMembershipRead`
 * projection, so a browser can neither read nor forge it directly.
 *
 * Two invariants this hook exists to hold:
 *
 *   • **Fail-open.** Any unknown — membership not yet read, a thread we have no
 *     row for, a failed write — resolves to "Prest0n answers". A silently mute
 *     assistant is a far worse failure than an over-eager one, and it is the
 *     specific risk this feature introduces.
 *   • **One-on-one is never suppressed.** The setting still exists and still
 *     reads "required" in a dedicated chat; it simply never gates there. See the
 *     carve-out reasoning in summonGate.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  parseSummonSettingInstruction,
  resolveSummonDecision,
  type SummonDecision,
} from '../../services/threadMentions/summonGate';
import {
  postGroupThreadMessage,
  setThreadAssistantEnabled,
  setThreadSummonRequirement,
  type GroupPostAttachment,
} from '../../repositories/threadMembershipWriteClient';
import { describeAddParticipantFailure } from '../../services/threadAddPeople/addParticipantFlow';
import { resolveSummonGateEnabled } from '../../services/threadMentions/summonFeatureFlags';
import type { ThreadMembershipSource } from '../../services/threadChoicePanel';
import type { MessageReplyRef } from '../../types/messageReply';

export interface UseThreadSummonSettingArgs {
  activeThreadId: string | null | undefined;
  selfId: string | null | undefined;
  /** Resolved account — gates the whole feature (default OFF). */
  accountId?: string | null;
  /** Test override for the account flag. */
  gateEnabledOverride?: boolean;
  membershipSource?: ThreadMembershipSource;
  /** threadId → participant count, already maintained by the host. */
  participantCounts?: Record<string, number>;
  /** threadId → explicit relaxation, from the membership projection. */
  requireMentionByThread?: Record<string, boolean>;
  /**
   * Called after the server ACCEPTS a change, so the host can update the
   * projection-derived state it owns.
   *
   * Without this the hook had no way to make its own write visible.
   * `membershipSource.refresh()` refetches that source's internal cache, but
   * `requireMentionByThread` is rebuilt by a separate effect the write does not
   * re-trigger — so the map stayed stale, and clearing the optimistic overlay
   * fell straight back to the OLD value. The control flipped back on the next
   * render (reported live 2026-08-07: "as soon as I click on the chatbot to
   * type the prompt, it automatically turns grey again").
   *
   * The write succeeded, so its value IS the stored value; handing it to the
   * host closes the loop without waiting on a re-read.
   */
  onPersisted?: (threadId: string, requireMention: boolean) => void;
  /**
   * threadId → explicit `false` when a permitted user REMOVED Prest0n from
   * the thread (2026-09-08); absent ⇒ Prest0n is in. From the membership
   * projection, same lossy-whitelist discipline as requireMentionByThread.
   */
  assistantEnabledByThread?: Record<string, boolean>;
  /** Close the loop after an accepted Prest0n add/remove (same reason as onPersisted). */
  onAssistantPersisted?: (threadId: string, enabled: boolean) => void;
}

export interface ThreadSummonSettingController {
  /** Account-scoped enablement. False ⇒ the gate never suppresses anything. */
  enabled: boolean;
  /** The active thread's effective setting (true = a mention is required). */
  requireMention: boolean;
  /** True when the setting actually gates anything here (i.e. ≥2 humans). */
  gatesThisThread: boolean;
  /** Distinct humans the registry reports for the active thread (0 = unknown). */
  humanParticipantCount: number;
  busy: boolean;
  notice: string | null;
  /** Decide whether one outgoing message should summon Prest0n. */
  decide: (text: string) => SummonDecision;
  /** Flip the setting for the active thread. */
  setRequireMention: (next: boolean) => void;
  /**
   * Is Prest0n IN the active thread (2026-09-08)? True by default; false only
   * after a permitted user removed it. Read by the gate, the header line and
   * the people panel.
   */
  assistantEnabled: boolean;
  assistantBusy: boolean;
  /** Put Prest0n in / take Prest0n out of the active thread (server-authorized). */
  setAssistantEnabled: (next: boolean) => void;
  /** Apply a plain-language instruction; returns what it did, or null. */
  applyInstruction: (text: string) => boolean | null;
  /**
   * Deliver a message that summons nobody (W3a). Returns true when the message
   * reached the other participants.
   *
   * `attachments` (2026-08-26) carries storage-backed files along with the
   * text. Until it existed the composer exempted every attachment send from
   * the gate rather than drop the file — the hole that let a message addressed
   * to a colleague draw a Prest0n reply in a mention-only thread.
   */
  postWithoutSummon: (
    text: string,
    messageId: string,
    attachments?: GroupPostAttachment[],
    /** Reply to a specific message (2026-09-15): the earlier message this post answers. */
    replyTo?: MessageReplyRef | null,
  ) => Promise<boolean>;
  /**
   * True until THIS USER has been shown the silence note in THIS thread
   * (localStorage-backed, so once means once across reloads too). The host
   * additionally requires the suppressed send to directly follow a Prest0n
   * turn — the case where the sender plausibly expected a reply — before it
   * renders the note and consumes the once via markSilenceExplained. An
   * unexplained silence reads as broken; a repeated explanation reads as nagging.
   */
  shouldExplainSilence: boolean;
  markSilenceExplained: () => void;
}

/**
 * Storage key for the once-per-USER-per-THREAD silence note (owner directive
 * 2026-08-08: "first time per user within the thread, not just first time in
 * the thread"). The note itself is a local system message only its sender
 * sees, so client-side tracking IS per-user — but keying by uid as well keeps
 * that true when several test accounts share one browser profile, and
 * persisting to localStorage keeps "one time only" true across reloads
 * (in-memory state forgot on every refresh).
 */
function silenceNoteStorageKey(selfId: string, threadId: string): string {
  return `prest0n:summon_silence_note:v1:${selfId}:${threadId}`;
}

export function useThreadSummonSetting(
  args: UseThreadSummonSettingArgs,
): ThreadSummonSettingController {
  const threadId = (args.activeThreadId ?? '').trim();
  const selfId = (args.selfId ?? '').trim();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  /** Optimistic overlay so the toggle responds before the round-trip lands. */
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const membershipRef = useRef(args.membershipSource);
  membershipRef.current = args.membershipSource;
  /** Read through a ref so a new callback identity never re-creates the setter. */
  const onPersistedRef = useRef(args.onPersisted);
  onPersistedRef.current = args.onPersisted;
  /**
   * Threads that have already shown the "only replies when mentioned" note —
   * session state for the current pass, backed by localStorage (see
   * silenceNoteStorageKey) so the once-ness survives reloads.
   */
  const [explained, setExplained] = useState<Record<string, boolean>>({});
  const explainedPersisted = useMemo(() => {
    if (!selfId || !threadId) return false;
    try {
      return typeof localStorage !== 'undefined'
        && localStorage.getItem(silenceNoteStorageKey(selfId, threadId)) === '1';
    } catch {
      return false;
    }
  }, [selfId, threadId]);

  const enabled = useMemo(
    () => resolveSummonGateEnabled({ accountId: args.accountId, override: args.gateEnabledOverride }),
    [args.accountId, args.gateEnabledOverride],
  );

  const humanParticipantCount = threadId ? args.participantCounts?.[threadId] ?? 0 : 0;
  const gatesThisThread = enabled && humanParticipantCount >= 2;

  const requireMention = useMemo(() => {
    if (!threadId) return true;
    if (threadId in pending) return pending[threadId];
    const stored = args.requireMentionByThread?.[threadId];
    // Absent ⇒ required: the stated default for new and existing threads.
    return stored !== false;
  }, [threadId, pending, args.requireMentionByThread]);

  /** Optimistic overlay for the Prest0n in/out toggle — same shape as `pending`. */
  const [assistantPending, setAssistantPending] = useState<Record<string, boolean>>({});
  const [assistantBusy, setAssistantBusy] = useState(false);
  const onAssistantPersistedRef = useRef(args.onAssistantPersisted);
  onAssistantPersistedRef.current = args.onAssistantPersisted;
  const assistantEnabled = useMemo(() => {
    if (!threadId) return true;
    if (threadId in assistantPending) return assistantPending[threadId];
    // Explicit false only; absent ⇒ Prest0n is in (every thread's default).
    return args.assistantEnabledByThread?.[threadId] !== false;
  }, [threadId, assistantPending, args.assistantEnabledByThread]);

  const decide = useCallback(
    (text: string): SummonDecision => {
      // A removed Prest0n is quiet whatever the mention flag says — the
      // removal is a registry fact, not a gate setting (2026-09-08).
      if (!assistantEnabled) return { summon: false, reason: 'assistant-removed' };
      // Flag OFF ⇒ the gate is inert and Prest0n answers exactly as today,
      // whatever the thread's stored setting says.
      if (!enabled) return { summon: true, reason: 'mention-not-required' };
      return resolveSummonDecision({ text, requireMention, humanParticipantCount, assistantEnabled });
    },
    [enabled, requireMention, humanParticipantCount, assistantEnabled],
  );

  const setAssistantEnabled = useCallback(
    (next: boolean) => {
      if (!threadId || assistantBusy) return;
      setAssistantBusy(true);
      setNotice(null);
      setAssistantPending((prev) => ({ ...prev, [threadId]: next }));
      void setThreadAssistantEnabled(threadId, next)
        .then(async (outcome) => {
          if (!outcome.ok) {
            setAssistantPending((prev) => {
              const copy = { ...prev };
              delete copy[threadId];
              return copy;
            });
            // The server's decision names the reason (authority, archived,
            // network) — never a bare "couldn't".
            setNotice(describeAddParticipantFailure({ decision: outcome.decision, step: 'add' }));
            return;
          }
          onAssistantPersistedRef.current?.(threadId, next);
          const src = membershipRef.current as
            | (ThreadMembershipSource & { refresh?: () => Promise<void> })
            | undefined;
          if (src && typeof src.refresh === 'function') {
            await src.refresh().catch(() => undefined);
          }
          setAssistantPending((prev) => {
            if (!(threadId in prev)) return prev;
            const copy = { ...prev };
            delete copy[threadId];
            return copy;
          });
        })
        .catch(() => {
          setAssistantPending((prev) => {
            const copy = { ...prev };
            delete copy[threadId];
            return copy;
          });
          setNotice("Couldn't change that — try again.");
        })
        .finally(() => setAssistantBusy(false));
    },
    [threadId, assistantBusy],
  );

  const postWithoutSummon = useCallback(
    async (
      text: string,
      messageId: string,
      attachments?: GroupPostAttachment[],
      replyTo?: MessageReplyRef | null,
    ): Promise<boolean> => {
      const tid = threadId;
      // An attachment-only send (files dropped with no caption) is a real
      // message — requiring text here would drop exactly the sends the gate
      // now covers.
      if (!tid || !messageId) return false;
      if (!text.trim() && !attachments?.length) return false;
      try {
        const outcome = replyTo
          ? await postGroupThreadMessage(tid, text, messageId, undefined, attachments, replyTo)
          : await postGroupThreadMessage(tid, text, messageId, undefined, attachments);
        if (!outcome.ok) {
          // The message did NOT reach the others. Say so rather than let it
          // look delivered — a silently dropped message is the worst outcome
          // this whole path could produce.
          setNotice("Your message didn't reach the others — try again.");
          return false;
        }
        // Deliberately does NOT mark the silence note as shown. The note only
        // renders when the send follows a Prest0n turn (the sender plausibly
        // expected a reply — host-side condition, owner directive 2026-08-08);
        // auto-marking on every successful post consumed the once-per-thread
        // budget on sends where no note appeared, so the note could never
        // show later when the condition finally held. Marking is the host's
        // job, via markSilenceExplained, at the moment the note is rendered.
        return true;
      } catch {
        setNotice("Your message didn't reach the others — try again.");
        return false;
      }
    },
    [threadId],
  );

  const markSilenceExplained = useCallback(() => {
    if (!threadId) return;
    setExplained((prev) => (prev[threadId] ? prev : { ...prev, [threadId]: true }));
    if (selfId) {
      try {
        localStorage.setItem(silenceNoteStorageKey(selfId, threadId), '1');
      } catch { /* session state above still holds the once-ness for this pass */ }
    }
  }, [threadId, selfId]);

  const setRequireMention = useCallback(
    (next: boolean) => {
      if (!threadId || busy) return;
      setBusy(true);
      setNotice(null);
      setPending((prev) => ({ ...prev, [threadId]: next }));
      void setThreadSummonRequirement(threadId, next)
        .then(async (outcome) => {
          if (!outcome.ok) {
            // Roll the optimistic overlay back — the stored value is authoritative.
            setPending((prev) => {
              const copy = { ...prev };
              delete copy[threadId];
              return copy;
            });
            setNotice("Couldn't change that setting — try again.");
            return;
          }
          // Tell the host FIRST, so the projection-derived state it owns
          // already agrees before the optimistic overlay is dropped below.
          // Reversing these two produces a visible flip back to the old value.
          onPersistedRef.current?.(threadId, next);
          const src = membershipRef.current as
            | (ThreadMembershipSource & { refresh?: () => Promise<void> })
            | undefined;
          if (src && typeof src.refresh === 'function') {
            await src.refresh().catch(() => undefined);
          }
          /**
           * Drop the optimistic overlay once the refreshed projection is in.
           *
           * It used to survive a successful write, and `requireMention` reads
           * `pending` BEFORE the stored value — so for the rest of the session
           * the client believed whatever was last clicked, whether or not the
           * server had actually stored it. Combined with the server never
           * persisting a setting-only change (fixed in the same pass), the two
           * disagreed permanently and the gate ran on a value that had never
           * round-tripped.
           *
           * Clearing it here makes the SERVER authoritative the moment its
           * answer is available, which is the only way a stale or rejected
           * write can become visible.
           */
          setPending((prev) => {
            if (!(threadId in prev)) return prev;
            const copy = { ...prev };
            delete copy[threadId];
            return copy;
          });
        })
        .catch(() => {
          setPending((prev) => {
            const copy = { ...prev };
            delete copy[threadId];
            return copy;
          });
          setNotice("Couldn't change that setting — try again.");
        })
        .finally(() => setBusy(false));
    },
    [threadId, busy],
  );

  const applyInstruction = useCallback(
    (text: string): boolean | null => {
      const parsed = parseSummonSettingInstruction(text);
      if (parsed === null) return null;
      if (parsed === requireMention) return parsed; // already in that state
      setRequireMention(parsed);
      return parsed;
    },
    [requireMention, setRequireMention],
  );

  return {
    enabled,
    requireMention,
    gatesThisThread,
    humanParticipantCount,
    busy,
    notice,
    decide,
    setRequireMention,
    assistantEnabled,
    assistantBusy,
    setAssistantEnabled,
    applyInstruction,
    postWithoutSummon,
    shouldExplainSilence:
      gatesThisThread && requireMention && !explained[threadId] && !explainedPersisted,
    markSilenceExplained,
  };
}
